/**
 * Auth Context
 * 
 * Provides user authentication state across the app
 * Similar to using Context API or Redux for state management
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import { userAPI, authAPI } from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = (newUser, newToken) => {
    setUser(newUser);
    setToken(newToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const updateToken = (newToken) => {
    if (!newToken) return;
    setToken(newToken);
    localStorage.setItem('token', newToken);
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch (_) { /* best-effort */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await userAPI.getProfile();
      if (res?.data) updateUser(res.data);
    } catch (error) {
      console.error('Failed to refresh user profile:', error.response?.data || error.message);
    }
  }, [updateUser]);

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // When the api interceptor silently refreshes the token it writes to
  // localStorage and dispatches 'auth:tokenRefreshed'. Sync context state here
  // so components reading `token` stay up to date.
  useEffect(() => {
    const onTokenRefreshed = () => {
      const newToken = localStorage.getItem('token');
      if (newToken) setToken(newToken);
    };

    // The interceptor dispatches this when a refresh succeeds
    window.addEventListener('auth:tokenRefreshed', onTokenRefreshed);

    // The interceptor dispatches this when refresh fails and the user is
    // redirected to /login — clear context state to match.
    const onSessionExpired = () => {
      setUser(null);
      setToken(null);
    };
    window.addEventListener('auth:sessionExpired', onSessionExpired);

    const onBalanceUpdated = () => {
      refreshUser();
    };
    window.addEventListener('user:balanceUpdated', onBalanceUpdated);

    return () => {
      window.removeEventListener('auth:tokenRefreshed', onTokenRefreshed);
      window.removeEventListener('auth:sessionExpired', onSessionExpired);
      window.removeEventListener('user:balanceUpdated', onBalanceUpdated);
    };
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, updateToken, logout, updateUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
